// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { getSettingsCollection } from "@eisinc/App/Store/Selectors/SettingsSelectors";
import CheckmarkGylph from "@eisinc/XDL/Icons/CheckmarkGylph";
import ShipsFromVendor from "@eisinc/XDL/Icons/ShipsFromVendor";
import { parseBoolean } from "@eisinc/XDL/Utilities";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import { FulfillmentMethod } from "@insite/client-framework/Services/SessionService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import getRealTimeWarehouseInventory from "@insite/client-framework/Store/Data/RealTimeInventory/Handlers/GetRealTimeWarehouseInventory";
import { getShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import translate from "@insite/client-framework/Translate";
import {
    AvailabilityDto,
    AvailabilityMessageType,
    ProductSettingsModel,
} from "@insite/client-framework/Types/ApiModels";
import { DataTableProps } from "@insite/mobius/DataTable";
import { DataTableBodyProps } from "@insite/mobius/DataTable/DataTableBody";
import { DataTableCellBaseProps } from "@insite/mobius/DataTable/DataTableCellBase";
import { DataTableHeadProps } from "@insite/mobius/DataTable/DataTableHead";
import { DataTableHeaderProps } from "@insite/mobius/DataTable/DataTableHeader";
import { DataTableRowProps } from "@insite/mobius/DataTable/DataTableRow";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import Icon, { IconPresentationProps, IconWrapper } from "@insite/mobius/Icon";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import { ModalPresentationProps } from "@insite/mobius/Modal";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { resolveColor, safeColor } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import styled, { css } from "styled-components";

// <XNG-Changes>
interface AdditionalResultsProps {
    pickupMessage?: string;
    isInStockAtPickUpWarehouse?: string;
    inventoryFallBack?: string;
    hideCheckAllWarehouseHyperLink?: string;
}
// </XNG-Changes>

interface OwnProps {
    productId: string;
    availability?: AvailabilityDto;
    unitOfMeasure: string;
    trackInventory: boolean;
    isProductDetailsPage?: boolean;
    failedToLoadInventory?: boolean;
    extendedStyles?: ProductAvailabilityStyles;
    // XNG-Changes
    additionalResults: AdditionalResultsProps;
    isFab?: boolean;
    productUrl?: string;
}

const mapStateToProps = (state: ApplicationState) => {
    const settings = getSettingsCollection(state);
    return {
        productSettings: settings.productSettings,
        showInventory: state.context.showInventory,
        // XNG-Changes
        customSettings: settings.customSettings as any,
        currentShipTo: getShipToState(state, getSession(state).shipToId).value,
        isPickUp: state.context.session.fulfillmentMethod === FulfillmentMethod.PickUp,
        enableRealTimeBomFabricationCheck: parseBoolean(settings.customSettings?.enableRealTimeBomFabricationCheck),
    };
};

const mapDispatchToProps = {
    getRealTimeWarehouseInventory,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface ProductAvailabilityStyles {
    container?: GridContainerProps;
    messageGridItem?: GridItemProps;
    availabilityByWarehouseLinkGridItem?: GridItemProps;
    inventoryMessage?: InjectableCss;
    messageText?: TypographyPresentationProps;
    realTimeText?: TypographyPresentationProps;
    errorWrapper?: InjectableCss;
    errorText?: TypographyPresentationProps;
    availabilityByWarehouseLink?: LinkPresentationProps;
    availabilityByWarehouseModal?: ModalPresentationProps;
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    availabilityByWarehouseTable?: DataTableProps;
    availabilityByWarehouseTableHead?: DataTableHeadProps;
    availabilityByWarehouseTableHeader?: DataTableHeaderProps;
    availabilityByWarehouseTableBody?: DataTableBodyProps;
    availabilityByWarehouseTableRow?: DataTableRowProps;
    availabilityByWarehouseTableCell?: DataTableCellBaseProps;
    // XNG-Changes
    pickupMessage?: TypographyPresentationProps;
    containerWithAdditionalMessage?: InjectableCss;
    additionalMessageContainer?: InjectableCss;
    tableContainer?: InjectableCss;
    iconStyles?: IconPresentationProps;
    fabProductPageLink?: LinkPresentationProps;
}

export const productAvailabilityStyles: ProductAvailabilityStyles = {
    container: {
        gap: 0,
        css: css`
            flex-grow: 0;
        `,
    },
    messageGridItem: { width: 12 },
    availabilityByWarehouseLinkGridItem: { width: 12 },
    availabilityByWarehouseModal: {
        size: 400,
        cssOverrides: {
            modalContent: css`
                padding: 0;
            `,
            modalTitle: css`
                padding: 10px 10px 10px 20px;
            `,
            headlineTypography: css`
                font-size: 20px;
                margin-bottom: 0;
            `,
        },
    },
    centeringWrapper: {
        css: css`
            height: 100px;
            width: 100%;
            display: flex;
            align-items: center;
        `,
    },
    spinner: {
        css: css`
            margin: 0 auto;
        `,
    },
    messageText: {
        css: css`
            width: 100%;
            ${wrapInContainerStyles}
        `,
    },
    realTimeText: {
        weight: "bold",
        css: css`
            display: inline-block;
            text-align: left;
            min-width: 25px;

            &::after {
                overflow: hidden;
                display: inline-block;
                vertical-align: bottom;
                animation: ellipsis steps(4, end) 900ms infinite 1s;
                content: "\\2026";
                width: 0;
            }

            @keyframes ellipsis {
                to {
                    width: 1.25em;
                }
            }
        `,
    },
    errorText: {
        weight: "bold",
    },
};

const InventoryMessage = styled.div<{ color: string }>`
    display: flex;
    // align-items: center;
    // padding: 5px;
    // border-radius: 5px;
    // background-color: ${props => safeColor(resolveColor(props.color, props.theme)).rgb().alpha(0.2).string()};
    // ${IconWrapper} {
    //     margin-right: 10px;
    // }
`;

const getAvailabilityColor = (productSettings: ProductSettingsModel, availability?: AvailabilityDto) => {
    if (!productSettings.showInventoryAvailability) {
        return "common";
    }
    // <XNG-Changes>
    if (availability?.message === translate("Ships From Vendor")) {
        return "info";
    }
    // </XNG-Changes>

    const availabilityType = availability?.messageType;
    if (availabilityType === AvailabilityMessageType.OutOfStock) {
        // XNG-Changes
        // return "danger";
        return "text";
    }

    // if (availabilityType === AvailabilityMessageType.LowStock) {
    //     return "warning";
    // }

    return "success";
};

const getAvailabilityIcon = (productSettings: ProductSettingsModel, availability?: AvailabilityDto) => {
    if (!productSettings.showInventoryAvailability) {
        return undefined;
    }
    // <XNG-Changes>
    if (availability?.message === translate("Ships From Vendor")) {
        return ShipsFromVendor;
    }
    // </XNG-Changes>

    const availabilityType = availability?.messageType;
    if (availabilityType === AvailabilityMessageType.OutOfStock) {
        // XNG-Changes
        // return X;
        return undefined;
    }

    // if (availabilityType === AvailabilityMessageType.LowStock) {
    //     return AlertTriangle;
    // }

    // return Check;
    return CheckmarkGylph;
};

// XNG-Changes : hide modals
// const showLink = (
//     availability: AvailabilityDto | undefined,
//     trackInventory: boolean,
//     productSettings: ProductSettingsModel,
//     isProductDetailsPage?: boolean,
// ): boolean => {
//     if (
//         !availability ||
//         availability.messageType === AvailabilityMessageType.NoMessage ||
//         !trackInventory ||
//         !productSettings.showInventoryAvailability ||
//         !productSettings.displayInventoryPerWarehouse
//     ) {
//         return false;
//     }

//     if (!productSettings.displayInventoryPerWarehouseOnlyOnProductDetail) {
//         return true;
//     }

//     return !!isProductDetailsPage;
// };

// <XNG-Changes> transform availability messages
const getTransformedAvailabilityMessage = (availability?: AvailabilityDto): string => {
    if (!availability || !availability.message) {
        return "";
    }

    const message = availability.message.toLowerCase();

    if (message.includes("in stock") || message.includes("low stock")) {
        return "In Stock | Ready to Ship";
    }

    if (message.includes("out of stock") || message.includes("temporarily")) {
        return "Available to Order";
    }

    return availability.message;
};

// </XNG-Changes>

const ProductAvailability = ({
    productId,
    availability,
    unitOfMeasure,
    trackInventory,
    productSettings,
    isProductDetailsPage,
    getRealTimeWarehouseInventory,
    failedToLoadInventory,
    showInventory,
    extendedStyles,
    // XNG-Changes
    additionalResults,
    currentShipTo,
    customSettings,
    isPickUp,
    isFab,
    enableRealTimeBomFabricationCheck,
    productUrl,
}: Props) => {
    // XNG-Changes : hide Modals
    // const [modalIsOpen, setModalIsOpen] = useState(false);
    // const [warehouses, setWarehouses] = useState<WarehouseDto[] | undefined>(undefined);
    // const [errorMessage, setErrorMessage] = useState("");
    // const [isLoading, setIsLoading] = useState(false);
    // const modalCloseHandler = () => {
    //     setModalIsOpen(false);
    // };
    // const viewAvailabilityByWarehouseClickHandler = () => {
    //     setIsLoading(true);
    //     setModalIsOpen(true);
    //     getRealTimeWarehouseInventory({
    //         productId,
    //         unitOfMeasure,
    //         expand: ["warehouses"],
    //         onComplete: result => {
    //             setWarehouses(result.warehouses);
    //             setErrorMessage(result.errorMessage);
    //             setIsLoading(false);
    //         },
    //     });
    // };

    const color = getAvailabilityColor(productSettings, availability);
    const iconSrc = getAvailabilityIcon(productSettings, availability);

    const [styles] = useState(() => mergeToNew(productAvailabilityStyles, extendedStyles));
    if (!showInventory) {
        return null;
    }

    if (failedToLoadInventory) {
        return (
            <StyledWrapper {...styles.errorWrapper}>
                <Typography {...styles.errorText} data-test-selector="inventoryLoadFailedMessage">
                    {siteMessage("RealTimeInventory_InventoryLoadFailed")}
                </Typography>
            </StyledWrapper>
        );
    }

    // <XNG-Changes>
    const showWarehouseAvailability = () => {
        if (customSettings.statesToRestrictInventoryToDefaultWarehouse.includes(currentShipTo?.state?.abbreviation)) {
            return false;
        }
        return true;
    };

    const showFabProductPageLink = !!isFab && !!enableRealTimeBomFabricationCheck && !isProductDetailsPage;

    // </XNG-Changes>

    let inventoryTextComponent = <Typography {...styles.realTimeText} />;
    if (showFabProductPageLink) {
        // <XNG-Changes>
        inventoryTextComponent = (
            <Link {...styles.fabProductPageLink} href={productUrl} data-test-selector="fabProductPageLink">
                {translate("Custom_FabProductInventoryMessage")}
            </Link>
        );
        // </XNG-Changes>
    } else if (
        availability?.messageType !== AvailabilityMessageType.NoMessage &&
        availability?.message?.length &&
        // XNG-Changes
        getTransformedAvailabilityMessage(availability)
    ) {
        inventoryTextComponent = (
            <InventoryMessage color={color} {...styles.inventoryMessage}>
                {/* XNG-Changes */}
                {iconSrc && <Icon {...styles.iconStyles} color={color} src={iconSrc} />}
                <Typography {...styles.messageText} data-test-selector="availabilityMessage">
                    {/* XNG-Changes */}
                    {/* {availability.message} */}
                    {getTransformedAvailabilityMessage(availability)}
                </Typography>
            </InventoryMessage>
        );
    } else if (availability) {
        inventoryTextComponent = <></>;
    }

    return (
        // XNG-Changes : change structure

        // <GridContainer {...styles.container}>
        //     <GridItem {...styles.messageGridItem}>{inventoryTextComponent}</GridItem>
        //     {showLink(availability, trackInventory, productSettings, isProductDetailsPage) && (
        //         <GridItem {...styles.availabilityByWarehouseLinkGridItem}>
        //             <Link
        //                 {...styles.availabilityByWarehouseLink}
        //                 onClick={viewAvailabilityByWarehouseClickHandler}
        //                 data-test-selector="availabilityByWarehouseLink"
        //             >
        //                 {translate("View Availability by Warehouse")}
        //             </Link>
        //             <Modal
        //                 {...styles.availabilityByWarehouseModal}
        //                 headline={translate("Warehouse Inventory")}
        //                 isOpen={modalIsOpen}
        //                 handleClose={modalCloseHandler}
        //             >
        //                 {isLoading && (
        //                     <StyledWrapper {...styles.centeringWrapper}>
        //                         <LoadingSpinner {...styles.spinner} />
        //                     </StyledWrapper>
        //                 )}
        //                 {!isLoading && (
        //                     <>
        //                         {errorMessage ? (
        //                             <div>{errorMessage}</div>
        //                         ) : (
        //                             <DataTable
        //                                 {...styles.availabilityByWarehouseTable}
        //                                 data-test-selector="availabilityByWarehouseModal"
        //                             >
        //                                 <DataTableHead {...styles.availabilityByWarehouseTableHead}>
        //                                     <DataTableHeader {...styles.availabilityByWarehouseTableHeader}>
        //                                         {translate("Warehouse")}
        //                                     </DataTableHeader>
        //                                     <DataTableHeader {...styles.availabilityByWarehouseTableHeader}>
        //                                         {translate("QTY")}
        //                                     </DataTableHeader>
        //                                 </DataTableHead>
        //                                 <DataTableBody {...styles.availabilityByWarehouseTableBody}>
        //                                     {warehouses?.map(warehouse => (
        //                                         <DataTableRow
        //                                             {...styles.availabilityByWarehouseTableRow}
        //                                             key={warehouse.name}
        //                                         >
        //                                             <DataTableCell
        //                                                 {...styles.availabilityByWarehouseTableCell}
        //                                                 data-test-selector="warehouseAvailabilityModal_Description"
        //                                             >
        //                                                 {warehouse.description || warehouse.name}
        //                                             </DataTableCell>
        //                                             <DataTableCell
        //                                                 {...styles.availabilityByWarehouseTableCell}
        //                                                 data-test-selector="warehouseAvailabilityModal_Quantity"
        //                                             >
        //                                                 {warehouse.qty}
        //                                             </DataTableCell>
        //                                         </DataTableRow>
        //                                     ))}
        //                                 </DataTableBody>
        //                             </DataTable>
        //                         )}
        //                     </>
        //                 )}
        //             </Modal>
        //         </GridItem>
        //     )}
        // </GridContainer>
        <>
            {additionalResults?.pickupMessage?.length && isPickUp ? (
                <StyledWrapper {...styles.containerWithAdditionalMessage}>
                    {showWarehouseAvailability() && <StyledWrapper>{inventoryTextComponent}</StyledWrapper>}
                    <StyledWrapper {...styles.additionalMessageContainer}>
                        <StyledWrapper>
                            <Typography {...styles.pickupMessage}>{additionalResults?.pickupMessage}</Typography>
                        </StyledWrapper>
                    </StyledWrapper>
                </StyledWrapper>
            ) : (
                <StyledWrapper {...styles.container}>
                    <StyledWrapper>{inventoryTextComponent}</StyledWrapper>
                </StyledWrapper>
            )}
        </>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(ProductAvailability);
